import { DOMUtils } from './DOM/DOMUtils';
import { TourTooltipController } from './react/tooltip/TourTooltipController';
import { TourBackdropController } from './react/backdrop/TourBackdropController';
import { TourConfig } from '../types/TourConfig';
import { IAssistantAction } from '../types/assistant';
import { TourHintController } from './react/hint/TourHintController';
import { DefaultTooltip } from './react/tooltip/default/DefaultTooltip';
import { TourTooltipDefaultArrow } from './react/tooltip/default/TourTooltipDefaultArrow';
import highlighter from './DOM/Highlighter';
import blinkingHighlighter from './DOM/BlinkingHighlighter';
import { TourLoaderController } from './react/loader/TourLoaderController';

export class TourViewer {
  private domUtils: DOMUtils;
  private tourTooltipController: TourTooltipController;
  private tourBackdropController: TourBackdropController;
  private tourHintController: TourHintController;
  private tourLoaderController: TourLoaderController;
  private unhighlightElementCallback: Function;
  private unhighlightBlinkingElementCallback: Function;

  constructor(private readonly tourConfig: TourConfig) {}

  init() {
    this.domUtils = new DOMUtils();
    this.domUtils.addReactContainerToDOM(this.rootElement);
    this.tourTooltipController = new TourTooltipController(this.domUtils);
    this.tourBackdropController = new TourBackdropController(this.domUtils);
    this.tourHintController = new TourHintController(this.domUtils);
    this.tourLoaderController = new TourLoaderController(this.domUtils);
  }

  private get rootElement(): HTMLElement {
    return this.tourConfig.rootElement || document.body;
  }

  get tooltip(): TourTooltipController {
    return this.tourTooltipController;
  }

  get backdrop(): TourBackdropController {
    return this.tourBackdropController;
  }

  get hint(): TourHintController {
    return this.tourHintController;
  }

  get loader(): TourLoaderController {
    return this.tourLoaderController;
  }

  hideAction(action: IAssistantAction) {
    if (typeof this.unhighlightElementCallback === 'function') {
      this.unhighlightElementCallback();
    }

    if (typeof this.unhighlightBlinkingElementCallback === 'function') {
      this.unhighlightBlinkingElementCallback();
    }

    if (action.tooltip && !action.tooltip.dontHideOnExit) {
      this.tooltip.hide();
      this.tooltip.destroy();
    }

    if (action.hint) {
      this.hint.destroy();
    }
  }

  destroyAction() {
    if (typeof this.unhighlightElementCallback === 'function') {
      this.unhighlightElementCallback();
    }

    if (typeof this.unhighlightBlinkingElementCallback === 'function') {
      this.unhighlightBlinkingElementCallback();
    }

    this.tooltip.destroy();

    this.backdrop.hide();

    this.loader.hide();
  }

  async renderTooltip(
    action: IAssistantAction,
    activeActionIndex: number,
    stopFn: () => void,
  ) {
    // Extract to function
    const toolTipComponent =
      action.tooltip.reactComponent ||
      this.tourConfig.tooltipComponent ||
      DefaultTooltip;

    const arrowComponent =
      action.tooltip.customArrowComponent ||
      this.tourConfig.customArrowComponent ||
      TourTooltipDefaultArrow;

    const content =
      typeof action.tooltip.content === "function"
        ? action.tooltip.content()
        : action.tooltip.content;
    const tooltipContent = this.tourConfig.translateFn
      ? this.tourConfig.translateFn(
          content,
          action.tooltip.contentTranslationOptions
        )
      : content;

    const tooltipTitle = this.tourConfig.translateFn
      ? this.tourConfig.translateFn(
          action.tooltip.title,
          action.tooltip.titleTranslationOptions,
        )
      : action.tooltip.title;

    if (this.tourConfig.highestZIndex) {
      if (!action.tooltip.customStyle) {
        action.tooltip.customStyle = {};
      }

      action.tooltip.customStyle.zIndex = this.tourConfig.highestZIndex;
    }

    const customProps = {
      ...this.tourConfig.customTooltipProps,
      ...action.tooltip.customProps,
    };

    this.tooltip
      .setTooltipState()
      .setStopTourFn(stopFn)
      .setShowCloseButton(action.tooltip.closeButton)
      .doNextIf(!!action.tooltip.clickableButtons)
      .setButtons(action.tooltip.clickableButtons)
      .setComponent(toolTipComponent)
      .setArrowComponent(arrowComponent)
      .setContent(tooltipContent)
      .setTitle(tooltipTitle)
      .setCustomProps(customProps)
      .setActiveAction(activeActionIndex)
      .setArrowPosition(action.tooltip.arrowPosition)
      .setCustomStyle(action.tooltip.customStyle)
      .doNextIf(action.tooltip.successMessage)
      .setSuccessMessage()
      .show()
      .apply();

    await this.tooltip.attachTo(
      action.element.HTMLElement,
      action.tooltip.positionX,
      action.tooltip.positionY,
      action.tooltip.offsetX,
      action.tooltip.offsetY,
      null,
      null,
      action.tooltip.alignTo,
    );

    if (action.tooltip.dontTrackChanges) {
      this.tooltip.detach();
    }

    this.tooltip
      .setTooltipState()
      .setAttachedTo(action.element.HTMLElement, action.tooltip.arrowPosition)
      //.unhideTooltip()
      .apply();

    // this.tourTooltip.addCustomClasses(action.tooltip.customClasses);
  }

  highlightElementByAction(action: IAssistantAction) {
    this.unhighlightElementCallback = highlighter.highlightElementByAction(
      action,
    );
  }

  blinkingHighlightElementByAction(action: IAssistantAction) {
    this.unhighlightBlinkingElementCallback = blinkingHighlighter.highlightElementByAction(
      action,
      this.tourConfig.blinkingHighlightColor,
    );
  }

  scrollToElement(action: IAssistantAction) {
    if (
      action &&
      action.scrollToElement !== false &&
      action.element &&
      action.element.HTMLElement
    ) {
      action.element.HTMLElement.scrollIntoView(false);
    }
  }
  async moveHintToElement(action: IAssistantAction) {
    if (action.hint) {
      this.tourHintController.show();
      await this.tourHintController.attachTo(
        // todo: check if we can realy remove action.hint.selector
        // action.hint.selector || (action.element && action.element.selector),
        action.element.HTMLElement,
        action.hint.positionX,
        action.hint.positionY,
      );
    }
  }
  async simulateClick() {
    await this.tourHintController.simulateClick();
  }
  fadeOutBackdrop(fadeOutTime) {}

  public disableHighlightInteractivity(action: IAssistantAction): void {
    if (action.element && action.element.HTMLElement) {
      action.element.HTMLElement.classList.add('tour-maker-disable-element');
    }

    if (action.additionalHighlightedElements) {
      action.additionalHighlightedElements.forEach((el) =>
        document
          .querySelectorAll<HTMLElement>(el)
          .forEach((x) => x.classList.add('tour-maker-disable-element')),
      );
    }
  }

  public enableHighlightInteractivity(action: IAssistantAction): void {
    if (action.element && action.element.HTMLElement) {
      action.element.HTMLElement.classList.remove('tour-maker-disable-element');
    }

    if (action.additionalHighlightedElements) {
      action.additionalHighlightedElements.forEach((el) =>
        document
          .querySelectorAll<HTMLElement>(el)
          .forEach((x) => x.classList.remove('tour-maker-disable-element')),
      );
    }
  }
  public addAdditionalClassesToElements(action: IAssistantAction): void {
    if (!action.additionalClassForElements) {
      return;
    }

    action.additionalClassForElements.forEach((item) => {
      const el = item.hook
        ? document.querySelectorAll<HTMLElement>(item.hook)
        : [action.element.HTMLElement];
      const classes = (item.className || '')
        .split(' ')
        .map((x) => x.trim())
        .filter((x) => !!x);
      el.forEach((x) => classes.forEach((cls) => x && x.classList.add(cls)));
    });
  }

  public removeAdditionalClassForElements(action: IAssistantAction): void {
    if (!action.additionalClassForElements) {
      return;
    }

    action.additionalClassForElements.forEach((item) => {
      const el = item.hook
        ? document.querySelectorAll<HTMLElement>(item.hook)
        : [action.element.HTMLElement];
      const classes = (item.className || '')
        .split(' ')
        .map((x) => x.trim())
        .filter((x) => !!x);
      el.forEach((x) => classes.forEach((cls) => x && x.classList.remove(cls)));
    });
  }

  public disableElements(elementsToDisable: HTMLElement[] = []): void {
    elementsToDisable.forEach((el) => {
      el.classList.add('tour-maker-disable-element');
      el.setAttribute('disabled', 'true');
    });
  }

  public enableElements(elementsToEnable: HTMLElement[] = []): void {
    elementsToEnable.forEach((el) => {
      el.classList.remove('tour-maker-disable-element');
      el.removeAttribute('disabled');
    });
  }
}
